import { z } from "zod";
import { moneySchema, quantitySchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
import { evChargeComponentSchema, evConnectorStatusSchema, evIdentificationSchema } from "./enums.js";
/**
 * Şarj — ajan ↔ bulut protokolü (Prompt 19, ADR 0019 §13 / §17, `docs/domain/agent-protocol.md` §2 "Prompt 19") ve API ↔ OCPP
 * süreci komut dürtüsü (`docs/domain/ocpp.md` §4). Ajan şarj ünitesiyle KONUŞMAZ: kasanın ödeme / başlatma ihtiyacı için durumu
 * buluttan yoklar; oturumlar ana veriye girmez.
 */
// ── API → OCPP süreci: komut kuyruğu dürtüsü (Redis pub/sub) ─────────────────
/** Redis kanalı: API komut satırını yazdıktan (commit) SONRA yayınlar; kaybolursa OCPP sürecinin yoklaması yakalar. */
export const EV_COMMAND_NUDGE_CHANNEL = "ocpp:commands";
export const evCommandNudgeSchema = z.object({
    tenantId: uuidSchema,
    chargerId: uuidSchema,
    commandId: uuidSchema,
});
// ── Ajan: GET /agent/v1/ev/state ─────────────────────────────────────────────
const kwhString = z.string().regex(/^\d{1,15}\.\d{3}$/);
const kwString = z.string().regex(/^\d{1,8}\.\d{2}$/);
export const agentEvActiveSessionSchema = z.object({
    sessionId: uuidSchema,
    startedAt: timestampSchema,
    energyKwh: kwhString,
    powerKw: kwString.nullable(),
    customerProfileId: uuidSchema.nullable(),
});
export const agentEvConnectorSchema = z.object({
    chargerId: uuidSchema,
    chargerName: z.string(),
    connectorNo: z.number().int().min(1).max(20),
    connectorType: z.enum(["CCS2", "TYPE2", "CHADEMO"]),
    powerKw: kwString,
    /** Son bilinen OCPP durumu; ünite hiç bağlanmadıysa `Unavailable`. */
    status: evConnectorStatusSchema,
    errorCode: z.string().nullable(),
    online: z.boolean(),
    activeSession: agentEvActiveSessionSchema.nullable(),
});
/** Kasada sepete eklenecek satır: ürün, miktar (kWh / dk), birim fiyat (₺, KDV dahil), brüt (kuruş). */
export const agentEvSessionLineSchema = z.object({
    component: evChargeComponentSchema,
    productId: uuidSchema,
    quantity: quantitySchema,
    unit: z.enum(["KWH", "MINUTE"]),
    unitPrice: unitPriceSchema,
    gross: moneySchema,
    vatRateCode: z.string(),
});
export const agentEvUnpaidSessionSchema = z.object({
    sessionId: uuidSchema,
    chargerName: z.string(),
    connectorNo: z.number().int().nullable(),
    startedAt: timestampSchema,
    completedAt: timestampSchema,
    energyKwh: kwhString,
    idleMinutes: z.number().int().nonnegative(),
    identifiedBy: evIdentificationSchema,
    customerProfileId: uuidSchema.nullable(),
    /** Satırların brüt toplamı (kuruş, KDV dahil). */
    total: moneySchema,
    lines: z.array(agentEvSessionLineSchema).min(1),
});
export const agentEvStateSchema = z.object({
    serverTime: timestampSchema,
    /** Opak özet (ETag): içerik değişmedikçe aynı; `If-None-Match` ile `304`. */
    revision: z.string().min(1).max(64),
    connectors: z.array(agentEvConnectorSchema),
    /** Tamamlanmış (tutarı kesin), ödenmemiş oturumlar — en eski önce, en çok 50. */
    unpaidSessions: z.array(agentEvUnpaidSessionSchema).max(50),
    /** Açık (canlı) oturumu olan tanınmış müşteriler — kampanya bağlamı `EV_CHARGING_ACTIVE`. */
    activeCustomerProfileIds: z.array(uuidSchema),
});
// ── Ajan: POST /agent/v1/ev/remote-start ─────────────────────────────────────
export const agentEvRemoteStartRequestSchema = z.object({
    chargerId: uuidSchema,
    connectorNo: z.number().int().min(1).max(20),
    /** Kasadaki personel (kasiyer); iz için komut satırına yazılır. */
    staffId: uuidSchema,
});
export const agentEvRemoteStartResponseSchema = z.object({
    idTag: z.string().regex(/^[A-Z0-9]{8,20}$/),
    tokenId: uuidSchema,
    commandId: uuidSchema,
    expiresAt: timestampSchema,
});
//# sourceMappingURL=protocol.js.map